import React from 'react';
import { connect } from 'react-redux';
import i18n from 'util/i18n';
import { api } from 'services/api';
import FileSaver from 'file-saver';
import { Modal } from 'components/Modal';
import StickyHeader from 'components/Sticky/StickyHeader';
import Logout from 'components/Navigation/Logout';
import classnames from 'classnames';
import * as formTypes from 'constants/formTypes';
import { DateTime } from 'luxon';
import Survey from 'components/Form/Survey';
import CreditFreezeBanner from 'components/CreditFreezeBanner';
import { shouldDisplayCreditFreeze } from 'helpers/form';
import AnnouncementBannerList from 'components/AnnouncementBannerList/AnnouncementBannerList';
import env from 'config/environment';
import PDFButton from './components/PDFButton';
import AttachmentList from './components/AttachmentList';

export const Locked = class Locked extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      attachmentsLoaded: false,
      showError: false,
      pdfStatus: 'PENDING',
      showPDFError: false,
      pdfExpiration: '',
      lastSubmissionDate: '',
      errorCode: 0,
    };
  }

  componentDidMount() {
    this.getStatus();
    this.getPDFStatus();

    api.listAttachments().then((resp) => {
      this.setState({
        attachmentList: resp.data,
        attachmentsLoaded: true,
      });
    }).catch((err) => console.error(err));
  }

  triggerErrorModal = (err) => {
    this.setState({
      showError: true,
      errorCode: err?.response?.status || 0,
    });
  };

  triggerPDFErrorModal = (err) => {
    this.setState({
      showPDFError: true,
    });
  };

  getForm = () => {
    api.getCompleteForm().then(
      (response) => {
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        FileSaver.saveAs(blob, 'export.pdf');
      },
      (err) => {
        this.triggerErrorModal(err);
      }
    ).catch((err) => console.error(err));
  };

  fetchAttachment = (id, filename) => {
    api.getAttachment(id).then((response) => {
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      FileSaver.saveAs(blob, filename);
    }, (err) => {
      this.triggerErrorModal(err);
    }).catch((err) => console.error(err));
  };

  getStatus = async () => {
    try {
      const response = await api.status();
      const pdfExpiration = response?.data?.PDFExpiration;
      if (pdfExpiration) {
        this.setState({ pdfExpiration });
      }
      const lastSubmissionDate = response?.data?.LastSubmissionDate;
      if (lastSubmissionDate) {
        this.setState({ lastSubmissionDate });
      }
    } catch (err) {
      console.log(err);
    }
  };

  getPDFStatus = async () => {
    try {
      const response = await api.getPDFStatus();
      const status = response?.data?.Status;
      if (status) {
        this.setState({
          pdfStatus: status,
        });
      }
      if (status !== 'AVAILABLE') {
        setTimeout(() => {
          this.getStatus();
          this.getPDFStatus();
        }, 5000);
      }
    } catch (err) {
      try {
        this.triggerPDFErrorModal(err);
      } catch (e) {
        console.error(e);
      }
    }
  };

  getLastSubmissionDate() {
    return this.state.lastSubmissionDate.replace(/:\d+\.\d+/g, '');
  }

  getSurveyUrl() {
    const defaultSetting = '__SURVEY_URL__';
    let result = env.SurveyUrl();
    if (result === defaultSetting) {
      result = 'https://touchpoints.app.cloud.gov/touchpoints/6be9ec2d';
    }
    if (!result || result?.toUpperCase() === 'DISABLE' || result === 'DISABLED' || result === 'FALSE') {
      result = '';
    }
    return result;
  }

  render() {
    const {
      formType, showSurvey, showSurveyModal, formVersion
    } = this.props;
    const { pdfExpiration } = this.state;
    const formName = formType === formTypes.SF852013 ? formTypes.SF85 : formTypes[formType]; // hardcode for SF85 2013
    const mobileNavigation = false;
    const titleClasses = classnames('eapp-structure-right', 'eapp-title');
    const headingClasses = classnames('eapp-structure-wrap', 'eapp-header', {
      'mobile-navigation': mobileNavigation,
    });
    const pdfExpirationDate = DateTime.fromISO(pdfExpiration).toLocaleString(DateTime.DATETIME_HUGE);
    const lastSubmissionDate = this.getLastSubmissionDate();
    const surveyUrl = this.getSurveyUrl();

    const displayCreditFreeze = shouldDisplayCreditFreeze(formType, formVersion);

    return (
      <div className="login eapp-core" id="login">
        <StickyHeader stickyClass="sticky-header">
          <div className="header-container">
            <div className="header">
              <header className="usa-header usa-header-basic" role="banner">
                <div className="usa-banner mobile-hidden">
                  <div className="usa-accordion">
                    <div className="usa-banner-header">
                      <div className="usa-grid usa-banner-inner">
                        <img src="/img/favicons/favicon-57.png" alt="U.S. flag" />
                        <p>{i18n.t('app.banner.title')}</p>
                        <button
                          type="button"
                          className="usa-accordion-button usa-banner-button"
                          aria-expanded="false"
                          aria-controls="gov-banner"
                        >
                          <span className="usa-banner-button-text">
                            {i18n.t('app.banner.button')}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div
                      className="usa-banner-content usa-grid usa-accordion-content"
                      id="gov-banner"
                    >
                      <div className="usa-banner-guidance-gov usa-width-one-half">
                        <img
                          className="usa-banner-icon usa-media_block-img"
                          src="/img/icon-dot-gov.svg"
                          alt="Dot gov"
                        />
                        <div className="usa-media_block-body">
                          <p>
                            <strong>{i18n.t('app.banner.witty')}</strong>
                            <br />
                            {i18n.t('app.banner.extension')}
                          </p>
                        </div>
                      </div>
                      <div className="usa-banner-guidance-ssl usa-width-one-half">
                        <img
                          className="usa-banner-icon usa-media_block-img"
                          src="/img/icon-https.svg"
                          alt="SSL"
                        />
                        <div className="usa-media_block-body">
                          <p>{i18n.t('app.banner.ssl')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={headingClasses}>
                  <div className="eapp-structure-row">
                    <div className="eapp-structure-left eapp-logo" id="logo">
                      <div className="eapp-logo-container">
                        <img
                          className="eapp-logo-icon"
                          src="/img/dcsa-logo.png"
                          alt="National Background Investigation Services"
                        />
                        <span className="eapp-logo-text">{formName}</span>
                      </div>
                    </div>
                    <div className={titleClasses}>
                      <div className="eapp-logout">
                        <Logout />
                      </div>
                      <h1 className="title">
                        <span className="title-text">
                          Questionnaire for National Background Investigation Services
                        </span>
                      </h1>
                    </div>
                  </div>
                </div>
              </header>
            </div>
          </div>
        </StickyHeader>
        {displayCreditFreeze && (
          <CreditFreezeBanner />
        )}
        <AnnouncementBannerList />
        <div className="content">
          <div className="table one">
            <div id="locked" className="locked">
              <h3>{i18n.t('login.locked.title')}</h3>
              {i18n.m('login.locked.para')}
              {i18n.m('login.locked.subtitle', { formName })}
              {this.state.lastSubmissionDate && (
                i18n.m('login.locked.timeSubmitted', { lastSubmissionDate })
              )}
              {(this.state.pdfStatus === 'PENDING' || !pdfExpiration)
                && i18n.m('login.locked.subtitlePending')}
              {this.state.pdfStatus === 'AVAILABLE' && pdfExpiration && (
                <div>
                  <PDFButton formName={formName} onClick={this.getForm} />
                  {i18n.t('login.locked.expiration')} {pdfExpirationDate}
                </div>
              )}
              <div className="pdf-downloads">
                <h4>{i18n.t('login.locked.certificates.title')}</h4>
                {i18n.m('login.locked.certificates.text')}
                {this.state.attachmentsLoaded && (
                  <AttachmentList
                    data={this.state.attachmentList}
                    fetchAttachment={this.fetchAttachment}
                  />
                )}
              </div>

              {showSurvey === true && surveyUrl !== '' && (
                <div className="survey-text">
                  {i18n.m('survey.contents')}
                  <div>
                    <a
                      href={surveyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 700 }}
                      type="button"
                    >
                      {i18n.t('survey.button.text')}
                    </a>
                  </div>
                </div>
              )}

            </div>
          </div>
          {showSurvey === true && showSurveyModal && surveyUrl !== '' && (
            <Survey
              className="mobile-hidden"
              showButton={false}
              initialShow={(!this.state.showError && !this.state.showPDFError)}
              surveyText="survey.submissionContents"
            />
          )}
          <Modal show={this.state.showError} className="locked-content">
            <h1>ERROR</h1>
            {this.state.errorCode === 403
              && (
                <p>
                  Sorry, you have already downloaded an archival copy during your current session. <br />
                  If you would like to download another copy you will need to log out and log back in to access another copy for download.
                </p>
              )}
            {this.state.errorCode !== 403
              && (
                <p>
                  There was a problem saving the PDF. Please contact your administrator if the problem
                  persists. <br /> Code {this.state.errorCode} at {(new Date()).toISOString()}
                </p>
              )}
            <button
              name="consent"
              type="button"
              className="consent-acceptance"
              title="Exit"
              onClick={() => this.setState({
                showError: false,
              })}
            >
              Exit
            </button>
          </Modal>
          <Modal show={this.state.showPDFError} className="locked-content">
            <h1>PDF ERROR</h1>
            <p>
              There was a problem retrieving copy of your completed form. Please refresh this page.
            </p>
            <button
              name="refresh"
              title="Refresh"
              type="button"
              onClick={() => {
                this.setState({
                  showPDFError: false,
                });

                window.location.reload();
              }}
            >
              Refresh
            </button>
            <button
              name="Exit"
              title="Exit"
              type="button"
              onClick={() => this.setState({
                showPDFError: false,
              })}
            >
              Exit
            </button>
          </Modal>
          <Modal show={this.state.showPDFError} className="locked-content">
            <h1>PDF ERROR</h1>
            <p>
              There was a problem retrieving copy of your completed form. Please refresh this page.
            </p>
            <button
              name="refresh"
              title="Refresh"
              type="button"
              onClick={() => {
                this.setState({
                  showPDFError: false,
                });

                window.location.reload();
              }}
            >
              Refresh
            </button>
            <button
              name="Exit"
              title="Exit"
              type="button"
              onClick={() => this.setState({
                showPDFError: false,
              })}
            >
              Exit
            </button>
          </Modal>
        </div>
      </div>
    );
  }
};

Locked.defaultProps = { showSurvey: true, showSurveyModal: false };

/**
 * @param {import('types/state').RootState} state
 */
function mapStateToProps(state) {
  return {
    formType: state.application.Settings.formType,
    formVersion: state.application.Settings.formVersion,
  };
}

// Wraps the App component with connect() which adds the dispatch()
// function to the props property for this component
export default connect(mapStateToProps)(Locked);
